import { useState } from "react";
import { Button } from "@/components/ui/button";
import { SectionError, SkeletonRows } from "@/components/ui/error-ui";
import { LedgerEntriesTable } from "@/components/ledger/LedgerEntriesTable";
import { LedgerEntryForm } from "@/components/ledger/LedgerEntryForm";
import { LedgerAccountsTable } from "@/components/ledger/LedgerAccountsTable";
import { useToast } from "@/components/ToastProvider";
import { useLedger } from "@/hooks/useLedger";
import type { LedgerAccountInput, LedgerEntryInput } from "@/types/ledger";

export function LedgerPanel() {
  const { accounts, journals, entries, loading, error, refresh, createEntry, createAccount, postEntry, hasArtist } = useLedger();
  const { push } = useToast();
  const [isEntryFormOpen, setEntryFormOpen] = useState(false);
  const [postingId, setPostingId] = useState<string | null>(null);

  const canCreateEntry = accounts.length > 0 && journals.length > 0 && hasArtist;

  const handlePost = async (entryId: string) => {
    setPostingId(entryId);
    try {
      await postEntry(entryId);
      push({ title: "Entry posted", message: "The journal entry is now locked." });
    } catch (err) {
      push({ title: "Posting failed", message: err instanceof Error ? err.message : "Unable to post entry" });
    } finally {
      setPostingId(null);
    }
  };

  const handleCreateEntry = async (payload: LedgerEntryInput) => {
    await createEntry(payload);
    push({ title: "Entry created", message: "Draft journal entry saved." });
  };

  const handleCreateAccount = async (payload: LedgerAccountInput) => {
    await createAccount(payload);
    push({ title: "Account added", message: `${payload.code} · ${payload.name}` });
  };

  return (
    <div className="space-y-6 rounded-2xl border border-border bg-background/70 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-primary-foreground dark:text-amber-100">Ledger</h2>
          <p className="text-sm text-muted-foreground">Double-entry accounting workspace with posting controls.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => refresh()} disabled={!hasArtist || loading}>
            Refresh
          </Button>
          <Button onClick={() => setEntryFormOpen(true)} disabled={!canCreateEntry}>
            New entry
          </Button>
        </div>
      </div>
      {!hasArtist && (
        <div className="rounded-2xl border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning">
          Select an artist profile in the header to load their ledger data.
        </div>
      )}
      {error && hasArtist && (
        <SectionError message={error} onRetry={() => refresh()} />
      )}
      {hasArtist && loading && !entries.length && (
        <SkeletonRows rows={6} />
      )}
      {hasArtist && !error && (
        <div className="grid gap-4 sm:gap-6 lg:grid-cols-[2fr_1fr]">
          <LedgerEntriesTable entries={entries} onPost={handlePost} postingId={postingId} loading={loading} />
          <LedgerAccountsTable accounts={accounts} onCreate={handleCreateAccount} />
        </div>
      )}
      <LedgerEntryForm
        open={isEntryFormOpen}
        onClose={() => setEntryFormOpen(false)}
        onSubmit={handleCreateEntry}
        accounts={accounts}
        journals={journals}
      />
    </div>
  );
}
